import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import classes from "./ProductFeatures.module.scss";
import CustomImage from "../../CustomImage";
import PlayIcon from "../../UI/Icons/PlayIcon";
import useIsMobile from "../../../hooks/useIsMobile";
import { getOnboardingOrFormCtaProps } from "../../../utils/applyCtaButtonProps";
import { persistSourceContext } from "../../../utils/b2c/sourceTracking";
import { PRIMARY_SOURCE_B2C_WIDGET } from "../../../utils/b2c/constants";
import { convertYouTubeToEmbed, getYouTubeVideoId } from "../../../utils/helpers";

interface ImageAsset {
  url: string;
  name?: string;
  alternativeText?: string;
}

interface ProductFeatureButton {
  title: string;
  link?: string | null;
  formId?: string | null;
  toOnboardingEnabled?: boolean | null;
  nearestOffice?: boolean | null;
  lookingForCoaching?: boolean | null;
}

interface ProductFeatureCard {
  id: number;
  title: string;
  description: string;
  highlightImg?: ImageAsset;
}

interface ProductFeaturesData {
  title: string;
  description: string;
  videoLink?: string;
  videoThumbnail?: ImageAsset;
  button?: ProductFeatureButton;
  card: ProductFeatureCard[];
  featureHeading: string;
  leftCardImg?: ImageAsset;
  rightCardImg?: ImageAsset;
}

interface Props {
  productFeaturesData?: ProductFeaturesData;
}

const ProductFeatures = ({ productFeaturesData }: Props) => {
  const router = useRouter();
  const isMobile = useIsMobile(768);
  const [hasStartedVideo, setHasStartedVideo] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoIframeRef = useRef<HTMLIFrameElement>(null);

  if (!productFeaturesData) return null;

  const { title, description, videoLink, videoThumbnail, button, card, featureHeading } = productFeaturesData;

  const videoId = getYouTubeVideoId(videoLink);
  const videoEmbedUrl = convertYouTubeToEmbed(videoLink);
  const videoPosterUrl = videoThumbnail?.url || (videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : "");
  const videoAutoplaySrc = videoEmbedUrl ? `${videoEmbedUrl}${videoEmbedUrl.includes("?") ? "&" : "?"}autoplay=1&enablejsapi=1` : "";

  useEffect(() => {
    if (!hasStartedVideo) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== "https://www.youtube.com") return;

      let data: any;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      const playerState = data?.info?.playerState;
      if (data?.event !== "infoDelivery" || playerState === undefined) return;

      if (playerState === 1) {
        setIsVideoPlaying(true);
      } else if (playerState === 0 || playerState === 2) {
        setIsVideoPlaying(false);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [hasStartedVideo]);

  const handleVideoIframeLoad = () => {
    videoIframeRef.current?.contentWindow?.postMessage(JSON.stringify({ event: "listening", id: videoId }), "https://www.youtube.com");
  };

  const ctaProps = getOnboardingOrFormCtaProps(router, {
    btnLink: button?.link,
    formId: button?.formId,
    toOnboardingEnabled: button?.toOnboardingEnabled ?? undefined,
    sourceUrl: typeof window !== "undefined" ? window.location.pathname : "",
  });

  const handleProductFeaturesCtaClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (ctaProps.mode === "onboarding") {
      persistSourceContext({
        sourceUrl: window.location.href,
        primarySource: PRIMARY_SOURCE_B2C_WIDGET,
        fifthStepVisible: button?.nearestOffice ?? true,
        coachingVisible: button?.lookingForCoaching ?? true,
      });
    }
    ctaProps.onClick?.(e);
  };

  return (
    <section className={classes["product-features-container"]}>
      <div className={classes.intro}>
        <div className={classes["intro-content"]}>
          {title && <div className={classes.heading} dangerouslySetInnerHTML={{ __html: title }} />}
          {description && <div className={classes.description} dangerouslySetInnerHTML={{ __html: description }} />}
          {button?.title && (
            <button type="button" className={`${classes["cta-button"]} ${ctaProps.formClassName ?? ""}`.trim()} onClick={handleProductFeaturesCtaClick}>
              {button.title}
            </button>
          )}
        </div>

        {videoLink && (
          <div className={classes.media}>
            {!isVideoPlaying && (
              <>
                <div className={classes["left-img-container"]}>
                  <CustomImage
                    src={productFeaturesData?.leftCardImg?.url ?? ""}
                    height={isMobile ? 85 : 176}
                    width={isMobile ? 85 : 176}
                    alt={productFeaturesData?.leftCardImg?.name ?? ""}
                  />
                </div>
                <div className={classes["right-img-container"]}>
                  <CustomImage
                    src={productFeaturesData?.rightCardImg?.url ?? ""}
                    height={isMobile ? 94 : 194}
                    width={isMobile ? 73 : 151}
                    alt={productFeaturesData?.rightCardImg?.name ?? ""}
                  />
                </div>
              </>
            )}

            <div className={classes["shadow-layer"]}></div>

            {videoLink && (
              <div className={classes["video-frame"]}>
                {hasStartedVideo && (
                  <iframe
                    ref={videoIframeRef}
                    className={classes.video}
                    src={videoAutoplaySrc}
                    title={title || "Product video"}
                    style={{ border: 0 }}
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    onLoad={handleVideoIframeLoad}
                  />
                )}

                {!hasStartedVideo && (
                  <>
                    {videoPosterUrl && (
                      <img
                        className={classes.video}
                        src={videoPosterUrl}
                        alt={videoThumbnail?.alternativeText || videoThumbnail?.name || title || "Product video"}
                        onClick={() => {
                          setHasStartedVideo(true);
                          setIsVideoPlaying(true);
                        }}
                      />
                    )}
                    <div className={classes["outer-radius"]}>
                      <button
                        type="button"
                        className={classes["play-button"]}
                        onClick={() => {
                          setHasStartedVideo(true);
                          setIsVideoPlaying(true);
                        }}
                        aria-label="Play video"
                      >
                        <PlayIcon />
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {card?.length > 0 && (
        <div className={classes["grid-section"]}>
          <h2 className={classes["grid-heading"]}>{featureHeading}</h2>

          <div className={classes["card-grid"]}>
            {card.map((item) => {
              return (
                <div key={item.id} className={classes.card}>
                  {item.highlightImg?.url && (
                    <div className={classes["card-image-wrapper"]}>
                      <CustomImage
                        src={item.highlightImg.url}
                        alt={item.highlightImg.alternativeText || item.title}
                        layout="responsive"
                        width={325}
                        height={252}
                        objectFit="contain"
                      />
                    </div>
                  )}
                  <div className={classes["card-content"]}>
                    <h3 className={classes["card-title"]}>{item.title}</h3>
                    <p className={classes["card-description"]}>{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

export default ProductFeatures;
